// useWhatsAppWebSessions — live view of a space's QR-channel sessions.
//
// Realtime pushes arrive on whatsapp_web_sessions; the payload is never used, the hook
// re-reads the explicit column list instead (the row also holds secret columns the
// screen must not touch). A polling fallback runs only while some session is still
// being linked (the states in which the QR changes), because a dropped Realtime socket
// must not leave a stale code on screen.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchWebSessions, isLinking, type WebSession } from '@/lib/whatsapp-web-api';

/** Refresh cadence of the fallback poll while a session is being linked. */
export const WEB_SESSION_POLL_MS = 4000;

/**
 * @param tenantId  A space id, or `undefined` with `allSpaces` for the platform-admin console.
 *   `null` means the space is not known yet: nothing is read.
 */
export function useWhatsAppWebSessions(tenantId: string | null | undefined, allSpaces = false) {
  const scope = allSpaces ? 'all' : tenantId;
  const [sessions, setSessions] = useState<WebSession[]>([]);
  const [loaded, setLoaded] = useState(false);
  const mounted = useRef(true);

  const reload = useCallback(async () => {
    if (!scope) return;
    try {
      const rows = await fetchWebSessions(scope === 'all' ? undefined : scope);
      if (mounted.current) setSessions(rows);
    } catch (error) {
      if (import.meta.env.DEV) console.error('[useWhatsAppWebSessions] read failed:', error);
    } finally {
      if (mounted.current) setLoaded(true);
    }
  }, [scope]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!scope) return undefined;
    void reload();
    let subscribedBefore = false;
    const channel = supabase
      .channel(`whatsapp-web-sessions:${scope}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'whatsapp_web_sessions',
          ...(scope === 'all' ? {} : { filter: `tenant_id=eq.${scope}` }),
        },
        () => {
          void reload();
        },
      )
      .subscribe((status) => {
        // Events pushed while the socket was down are gone: every (re)subscription re-reads.
        if (status === 'SUBSCRIBED') {
          if (subscribedBefore) void reload();
          subscribedBefore = true;
        }
      });
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [scope, reload]);

  // Linking, or an unlink the bridge has not yet confirmed.
  const anyLinking = sessions.some((s) => isLinking(s.status) || (s.desired_state === 'unlinked' && s.status === 'connected'));
  useEffect(() => {
    if (!anyLinking || !scope) return undefined;
    const timer = setInterval(() => void reload(), WEB_SESSION_POLL_MS);
    return () => clearInterval(timer);
  }, [anyLinking, scope, reload]);

  const byConfigId = useMemo(() => new Map(sessions.map((s) => [s.api_configuration_id, s])), [sessions]);

  return { sessions, byConfigId, loaded, reload };
}
